import { colors, priceText } from '@navicosa/ui-tokens';
import { useQuery } from '@tanstack/react-query';
import { Alert, Descriptions, Drawer, Empty, Image, Spin, Tag, Typography } from 'antd';
import { api } from '../../api/client';

interface PreviewData {
  status: string;
  name: string;
  product_code: string;
  model: string | null;
  brand: { name: string } | null;
  image: { url: string; alt: string } | null;
  price_label: 'reference_price' | 'contact_for_quote' | 'paused';
  prices: { rental_period: string; display: string }[];
  price_note: string;
  specifications: { key: string; label: string; display: string }[];
  service_areas: { id: string; name: string }[];
  short_description: string | null;
}

const LABEL = {
  reference_price: 'Giá tham khảo',
  contact_for_quote: 'Liên hệ báo giá',
  paused: 'Tạm ngừng nhận yêu cầu',
};

/** Xem trước dữ liệu đúng như Public API trả cho Mini App (PRD 6.3) – khung 390px như điện thoại. */
export function PreviewDrawer({
  productId,
  open,
  onClose,
}: {
  productId: string;
  open: boolean;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['preview', productId, open],
    queryFn: () => api.get<PreviewData>(`/admin/products/${productId}/preview`),
    enabled: open,
  });
  const d = q.data;
  return (
    <Drawer title="Xem trước như khách thấy" width={430} open={open} onClose={onClose}>
      {q.isLoading || !d ? (
        <Spin />
      ) : (
        <div style={{ maxWidth: 390 }}>
          {d.status !== 'published' && (
            <Alert
              type="warning"
              showIcon
              message="Sản phẩm chưa xuất bản – khách chưa thấy"
              style={{ marginBottom: 12 }}
            />
          )}
          {d.image ? (
            <Image
              src={d.image.url}
              alt={d.image.alt}
              style={{
                width: '100%',
                aspectRatio: '4 / 5',
                objectFit: 'contain',
                background: '#fff',
                borderRadius: 16,
              }}
            />
          ) : (
            <div
              style={{
                aspectRatio: '4 / 5',
                background: colors.brand50,
                borderRadius: 16,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Typography.Text type="secondary">Ảnh giữ chỗ</Typography.Text>
            </div>
          )}
          <Typography.Title level={4} style={{ marginTop: 12, marginBottom: 0 }}>
            {d.name}
          </Typography.Title>
          <Typography.Text type="secondary">
            {[d.brand?.name, d.model, d.product_code].filter(Boolean).join(' · ')}
          </Typography.Text>
          <div style={{ margin: '8px 0' }}>
            <Tag
              color={
                d.price_label === 'reference_price'
                  ? 'orange'
                  : d.price_label === 'paused'
                    ? 'red'
                    : 'default'
              }
            >
              {LABEL[d.price_label]}
            </Tag>
          </div>
          {d.prices.length > 0 ? (
            <Descriptions column={1} size="small" bordered>
              {d.prices.map((p) => (
                <Descriptions.Item
                  key={p.rental_period}
                  label={{ day: 'Ngày', week: 'Tuần', month: 'Tháng' }[p.rental_period]}
                >
                  <span
                    style={{
                      color: priceText.color,
                      fontWeight: priceText.weight,
                      fontSize: priceText.size,
                    }}
                  >
                    {p.display}
                  </span>
                </Descriptions.Item>
              ))}
            </Descriptions>
          ) : null}
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
            {d.price_note}
          </Typography.Paragraph>
          <Typography.Title level={5}>Thông số kỹ thuật</Typography.Title>
          {d.specifications.length ? (
            <Descriptions column={1} size="small" bordered>
              {d.specifications.map((s) => (
                <Descriptions.Item key={s.key} label={s.label}>
                  {s.display}
                </Descriptions.Item>
              ))}
            </Descriptions>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có thông số" />
          )}
          <Typography.Title level={5} style={{ marginTop: 16 }}>
            Khu vực phục vụ
          </Typography.Title>
          <Typography.Text>{d.service_areas.map((a) => a.name).join(', ') || '—'}</Typography.Text>
        </div>
      )}
    </Drawer>
  );
}
